/**
 * Mock Universal Login context for local dev when Auth0 context is not available.
 * Sets window.universal_login_context so ACUL SDK hooks (useLoginId, useScreen, etc.) work.
 * Structure must match Auth0's expected format (snake_case for raw API).
 *
 * When switching screens, we must reload the page because the ACUL SDK caches
 * BaseContext.context and the screen instance; they don't refresh when the mock changes.
 */

export const DEV_SCREEN_STORAGE_KEY = "acul-dev-screen";
export const DEV_LOCALE_STORAGE_KEY = "acul-dev-locale";

export type ScreenName =
  | "login-id"
  | "login-password"
  | "mfa-sms-challenge"
  | "signup"
  | "signup-id"
  | "signup-password";
export type Locale = "en" | "id";

function baseContext(screenName: string, promptName: string, locale: Locale = "en") {
  return {
    branding: { settings: {}, themes: { default: {} } },
    client: { id: "dev-client", name: "Propsman Admin", logo_uri: "" },
    organization: null,
    prompt: { name: promptName },
    screen: {
      name: screenName,
      links: {
        reset_password: "/reset-password",
        signup: "/signup",
        edit_identifier: "/login-id",
      },
    },
    tenant: {
      name: "propsman-dev",
      enabled_locales: ["en", "id"],
      friendly_name: "Propsman",
    },
    transaction: {
      state: "dev-mock-state",
      locale,
      connection: {
        name: "Username-Password-Authentication",
        strategy: "auth0",
        options: {
          forgot_password_enabled: true,
          signup_enabled: true,
          attributes: { email: { identifier_active: true }, username: { identifier_active: false } },
          authentication_methods: {
            password: { enabled: true },
            passkey: { enabled: false },
          },
        },
      },
      alternate_connections: [
        { name: "google-oauth2", display_name: "Google" },
        { name: "facebook", display_name: "Facebook" },
        { name: "apple", display_name: "Apple" },
      ],
    },
    untrusted_data: {},
  };
}

export function setDevMockContext(
  screenName: ScreenName,
  locale?: Locale
): void {
  if (typeof window === "undefined") return;

  const win = window as { universal_login_context?: object };
  const resolvedLocale: Locale =
    locale ?? (sessionStorage?.getItem(DEV_LOCALE_STORAGE_KEY) as Locale | null) ?? "en";

  switch (screenName) {
    case "login-id":
      win.universal_login_context = baseContext("login-id", "login-id", resolvedLocale);
      break;
    case "login-password":
      win.universal_login_context = {
        ...baseContext("login-password", "login-password", resolvedLocale),
        screen: {
          name: "login-password",
          links: {
            reset_password: "/reset-password",
            signup: "/signup",
            edit_identifier: "/login-id",
          },
        },
      };
      break;
    case "mfa-sms-challenge":
      win.universal_login_context = {
        ...baseContext("mfa-sms-challenge", "mfa-sms", resolvedLocale),
        screen: {
          name: "mfa-sms-challenge",
          links: {},
          data: { phone_number: "••••••1360", show_remember_device: true, show_link_voice: true },
        },
        transaction: {
          ...baseContext("mfa-sms-challenge", "mfa-sms", resolvedLocale).transaction,
          connection: { name: "sms", strategy: "sms" },
        },
      };
      break;
    case "signup":
      win.universal_login_context = {
        ...baseContext("signup", "signup", resolvedLocale),
        screen: {
          name: "signup",
          links: {
            reset_password: "/reset-password",
            signup: "/signup",
            edit_identifier: "/signup-id",
            login: "/login-id",
          },
        },
        transaction: {
          ...baseContext("signup", "signup", resolvedLocale).transaction,
          required_identifiers: ["email"],
          optional_identifiers: [],
        },
      };
      break;
    case "signup-id":
      win.universal_login_context = {
        ...baseContext("signup-id", "signup-id", resolvedLocale),
        screen: {
          name: "signup-id",
          links: {
            reset_password: "/reset-password",
            signup: "/signup",
            edit_identifier: "/signup-id",
            login: "/login-id",
          },
        },
        transaction: {
          ...baseContext("signup-id", "signup-id", resolvedLocale).transaction,
          required_identifiers: ["email"],
          optional_identifiers: [],
        },
      };
      break;
    case "signup-password":
      win.universal_login_context = {
        ...baseContext("signup-password", "signup-password", resolvedLocale),
        screen: {
          name: "signup-password",
          links: {
            reset_password: "/reset-password",
            signup: "/signup",
            edit_identifier: "/signup-id",
            login: "/login-id",
          },
          data: { email: "user@example.com", phone_number: null, username: null },
        },
        transaction: {
          ...baseContext("signup-password", "signup-password", resolvedLocale).transaction,
          required_identifiers: ["email"],
          optional_identifiers: [],
        },
      };
      break;
    default:
      win.universal_login_context = baseContext("login-id", "login-id", resolvedLocale);
  }
}

export function hasDevContext(): boolean {
  if (typeof window === "undefined") return false;
  const win = window as { universal_login_context?: unknown };
  return !!win.universal_login_context;
}
